import { createPart, type PagePart, type PartType } from '@/features/storefront/sections/parts';
import { defaultSection } from '@/features/storefront/sections/pages';
import {
  createSection,
  featureBlockSchema,
  type Section,
} from '@/features/storefront/sections/schema';

/**
 * Hyper's product page, written down.
 *
 * This theme used to *draw* its product page: a counter and a magazine, two
 * components and eleven hard-coded bands that no merchant could move, reorder
 * or delete. None of it is markup any more. The counter is a list of
 * components in four regions and the magazine is a stack of ordinary
 * sections, and both are *defaults* - what the page resolves to for a merchant
 * who has never arranged it, and what the first edit writes into their
 * document as their own.
 *
 * What these defaults may *say* is the other half of the story. A default is
 * what every Hyper store shows on every product page until the merchant
 * changes it - a live storefront, not a preview - so it carries nothing that
 * belongs to the sample shop the theme was drawn from: no photographs from the
 * furniture shoot, no quotes from its customers, no discount code, no
 * showroom, no warranty the merchant never offered. Every band below either
 * draws the merchant's own data (their products, their categories) or says
 * something true of any shop on the platform. The layout feel stays: the
 * grey-tile bands, the dense grids, the transaction column.
 *
 * - every band under the product is a section a merchant can hide, reorder,
 *   duplicate, restyle or throw away, and every word in it is a field in the
 *   inspector rather than a string in a `.tsx` file;
 * - every piece of the counter is a component in a region, so "put the
 *   quantity above the options" and "take the payment panel off" are drags in
 *   the tree rather than feature requests;
 * - nothing here is special-cased anywhere: the storefront renders these
 *   through the same `SectionList` and the same `PartRegion` every other page
 *   in the shop goes through, and the customiser edits them through the same
 *   builder. See `sections/pageDocument.ts` and `sections/partDocument.ts` for
 *   how a default becomes a merchant's own.
 */

/* --- The counter ---------------------------------------------------------- */

/**
 * One component of the page, with an id that does not change between renders.
 *
 * `createPart` generates a fresh id every call, which is right for the builder
 * and wrong for a default: defaults are recomputed whenever the document's
 * reference changes, and a component whose id changed is one React unmounts and
 * mounts again - which on this page would close every folded panel on every
 * keystroke in the customiser. Named rather than derived from the type, because
 * two of these could be the same type in two different columns.
 */
function part(
  id: string,
  type: PartType,
  region: string,
  over: Partial<Omit<PagePart, 'id' | 'type'>> = {},
): PagePart {
  const base = createPart(type, region);
  return {
    ...base,
    ...over,
    id: `hyper-${id}`,
    region,
    options: { ...base.options, ...(over.options ?? {}) },
  };
}

/**
 * The counter, as the components it is made of.
 *
 * The left column is the photograph with the facts folded up underneath it;
 * the right column is the transaction - price, options, how many, the ways
 * out. The band beneath both is the comparison and the suggestions, which are
 * the two things down there that need the product rather than the merchant.
 *
 * The delivery panel is laid out with no words of its own: it draws nothing
 * until the merchant writes their delivery terms into it, because a promise
 * about shipping is exactly the kind of sentence only they can make.
 */
export function hyperProductParts(): PagePart[] {
  return [
    // Above both columns, at the page's own width.
    part('breadcrumbs', 'product_breadcrumbs', 'top'),

    // The gallery column: the photograph, then the folded facts.
    part('gallery', 'product_gallery', 'gallery'),
    part('specs', 'product_specs', 'gallery', {
      label: 'Specifications',
      space_above: 'lg',
    }),
    part('description', 'product_description', 'gallery', {
      label: 'Product details',
      options: { show_heading: true, collapsible: true },
    }),
    part('payment', 'product_payment', 'gallery', { options: { collapsible: true } }),

    // The buy column: everything between seeing it and owning it.
    part('title', 'product_title', 'main'),
    part('brand', 'product_brand', 'main'),
    part('price', 'product_price', 'main', {
      options: { show_compare_at: true, show_saving: true },
    }),
    part('availability', 'product_availability', 'main', {
      options: {
        in_stock_text: 'In stock',
        out_of_stock_text: 'Out of stock in this combination',
      },
    }),
    part('summary', 'product_short_description', 'main'),
    // A row per axis with the values as dots: eleven colours in two sizes is
    // twenty-two buttons reading "Sage / Large" any other way.
    part('variants', 'product_variants', 'main', { options: { style: 'swatches' } }),
    part('quantity', 'product_quantity', 'main'),
    part('add', 'product_add_to_cart', 'main', { width: 'full' }),
    part('buy', 'product_buy_now', 'main', { width: 'full', options: { style: 'solid' } }),
    part('whatsapp', 'product_buy_on_whatsapp', 'main', { width: 'full' }),
    part('delivery', 'product_shipping_info', 'main', {
      label: 'Delivery and returns',
      body: '',
      options: { collapsible: false, icon: 'truck' },
    }),
    part('share', 'product_share', 'main'),

    // Under both columns: the two bands that are about this product rather
    // than about the shop.
    part('compare', 'product_comparison', 'below', { label: 'Compare with similar items' }),
    part('also-like', 'product_related', 'below'),
  ];
}

/* --- The magazine --------------------------------------------------------- */

/**
 * A section with an id that does not change between renders, for the same
 * reason {@link part} fixes one: a default is recomputed, and a section whose
 * id changed is a section the customiser loses its selection on.
 */
function fixed(id: string, section: Section): Section {
  return { ...section, id: `hyper-${id}` };
}

/** A dense shelf of the merchant's own newest products. */
export function hyperMoreToExplore(): Section {
  return fixed('more-to-explore', {
    ...createSection('featured_products'),
    title: 'More to explore',
    limit: 8,
    columns: 4,
    padding: 'lg',
  });
}

/** The merchant's own departments, as the rail the masthead lists them in. */
export function hyperDepartments(): Section {
  return fixed('departments', {
    ...createSection('categories'),
    title: 'Shop by department',
    // Five, not the six the masthead's rail runs at: five is the widest grid
    // a stored document may ask for, and a default becomes stored the moment
    // the merchant edits anything on the page.
    columns: 5,
    background: 'surface',
    padding: 'lg',
  });
}

/**
 * Three reassurances at the foot of every product page, in words true of any
 * shop on the platform - no free-shipping threshold, no return window, no
 * guarantee the merchant has not chosen to give.
 */
export function hyperPromises(): Section {
  return fixed('promises', {
    ...createSection('features'),
    title: 'Shop with confidence',
    columns: 3,
    padding: 'lg',
    blocks: [
      featureBlockSchema.parse({
        id: 'hyper-promise-checkout',
        icon: 'shield',
        title: 'Secure checkout',
        body: 'Payments are handled by a secure payment provider.',
      }),
      featureBlockSchema.parse({
        id: 'hyper-promise-updates',
        icon: 'package',
        title: 'Order updates',
        body: 'You hear from us when your order is confirmed and when it is on its way.',
      }),
      featureBlockSchema.parse({
        id: 'hyper-promise-help',
        icon: 'support',
        title: 'Here to help',
        body: 'Questions about this product? Get in touch and we will answer.',
      }),
    ],
  });
}

/**
 * The product page as a merchant on this theme first finds it.
 *
 * The product itself, then the bands that keep a shopper in the shop: more of
 * the range, the departments, and the reassurance strip. The two bands that
 * need the product rather than the shop - the comparison and the suggestions -
 * are components inside the anchor rather than sections after it, which is why
 * they stand directly under the buy column.
 */
export function hyperProductSections(): Section[] {
  return [
    defaultSection('product_details'),
    hyperMoreToExplore(),
    hyperDepartments(),
    hyperPromises(),
  ];
}
